import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/base/Seo';
import ComicCard from '@/components/base/ComicCard';
import { CATEGORIES, COMICS } from '@/mocks/comics';

function formatViews(n: number) {
  if (n >= 10000) return `${(n / 10000).toFixed(1).replace(/\.0$/, '')}万`;
  return `${n}`;
}

export default function ArticlePage() {
  const { id } = useParams();
  const comic = COMICS.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <section className="flex min-h-[70vh] flex-col items-center justify-center bg-background-50 px-4 pt-20 text-center">
          <i className="ri-emotion-sad-line text-6xl text-background-300"></i>
          <h1 className="mt-4 font-heading text-3xl text-foreground-950">没有找到这部作品</h1>
          <p className="mt-2 text-foreground-600">作品可能已被下架或链接有误。</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50">
            <i className="ri-arrow-left-line"></i>返回首页
          </Link>
        </section>
        <Footer />
      </>
    );
  }

  const related = COMICS.filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id).slice(0, 4);
  const category = CATEGORIES.find((c) => c.slug === comic.categorySlug);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.manhuadao.cn/' },
        { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `https://www.manhuadao.cn/category/${comic.categorySlug}` },
        { '@type': 'ListItem', position: 3, name: comic.title },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title}漫画：${comic.short}`,
      description: comic.short,
      image: comic.cover,
      author: { '@type': 'Organization', name: '漫画岛' },
      publisher: { '@type': 'Organization', name: '漫画岛' },
      datePublished: comic.updated,
      dateModified: comic.updated,
      mainEntityOfPage: `https://www.manhuadao.cn/article/${comic.id}`,
      keywords: [comic.title, `${comic.category}漫画`, ...comic.tags].join(','),
    },
  ];

  return (
    <>
      <Seo
        title={`${comic.title} · ${comic.category}漫画 | 漫画岛`}
        description={`${comic.short} 漫画岛推荐${comic.category}漫画《${comic.title}》，作者${comic.author}，评分${comic.rating}，已更新${comic.chapterCount}话。`}
        canonicalPath={`/article/${comic.id}`}
        jsonLd={jsonLd}
        type="article"
      />
      <Navbar />

      <section className="bg-gradient-to-b from-primary-600 to-primary-700 pt-24 pb-10 md:pt-28 md:pb-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-1.5 text-sm text-background-100/80">
            <Link to="/" className="flex items-center gap-1 hover:text-background-50">
              <i className="ri-home-4-line"></i>首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${comic.categorySlug}`} className="hover:text-background-50">
              {comic.category}漫画
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{comic.title}</span>
          </nav>

          <div className="flex flex-col gap-8 md:flex-row md:items-start">
            <div className="w-48 shrink-0 md:w-56">
              <img
                src={comic.cover}
                alt={`${comic.title}漫画封面`}
                title={`${comic.title} · ${comic.category}漫画`}
                className="w-full rounded-xl object-cover object-top shadow-sm"
              />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-background-50/15 px-3 py-1 text-sm font-bold text-background-50 backdrop-blur">{comic.category}</span>
                <span className="rounded-md bg-accent-500 px-3 py-1 text-sm font-bold text-background-50">{comic.status}</span>
                {comic.newArrival && <span className="rounded-md bg-background-50 px-3 py-1 text-sm font-bold text-primary-600">新作尝鲜</span>}
              </div>
              <h1 className="mt-3 font-heading text-3xl leading-tight text-background-50 md:text-5xl">{comic.title}</h1>
              <p className="mt-2 text-base text-background-100/90">作者：{comic.author} · 更新时间：{comic.updated}</p>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-background-100/90">{comic.short}</p>

              <div className="mt-5 flex flex-wrap gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-50/10 px-3 py-1 text-xs text-background-50 backdrop-blur">
                    #{t}
                  </span>
                ))}
              </div>

              <div className="mt-6 grid max-w-md grid-cols-3 gap-4">
                <div className="rounded-xl bg-background-50/10 p-3 text-center backdrop-blur">
                  <p className="font-heading text-2xl text-accent-300">{comic.rating}</p>
                  <p className="text-xs text-background-200">评分</p>
                </div>
                <div className="rounded-xl bg-background-50/10 p-3 text-center backdrop-blur">
                  <p className="font-heading text-2xl text-background-50">{comic.chapterCount}</p>
                  <p className="text-xs text-background-200">已更话数</p>
                </div>
                <div className="rounded-xl bg-background-50/10 p-3 text-center backdrop-blur">
                  <p className="font-heading text-2xl text-background-50">{formatViews(comic.views)}</p>
                  <p className="text-xs text-background-200">人气阅读</p>
                </div>
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-bold text-primary-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-book-2-line text-base"></i>
                  阅读最新话
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-bold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                >
                  <i className="ri-heart-line text-base"></i>
                  收藏作品
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-bold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                >
                  <i className="ri-share-line text-base"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-2xl text-foreground-950">
                <i className="ri-file-text-line text-primary-600"></i>
                作品简介
              </h2>
              <div className="mt-5 flex flex-col gap-4">
                {comic.intro.map((p, i) => (
                  <p key={i} className="text-base leading-loose text-foreground-700">
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-2xl text-foreground-950">
                <i className="ri-list-ordered-2 text-primary-600"></i>
                章节列表
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {Array.from({ length: Math.min(comic.chapterCount, 8) }).map((_, i) => {
                  const num = comic.chapterCount - i;
                  return (
                    <button
                      key={num}
                      type="button"
                      className="flex items-center justify-between rounded-lg bg-background-100 px-4 py-3 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <span className="flex items-center gap-2">
                        <i className="ri-play-circle-line"></i>
                        第 {num} 话
                      </span>
                      <span className="text-xs text-foreground-400">免费</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6">
              <h2 className="font-heading text-xl text-foreground-950">作品信息</h2>
              <dl className="mt-4 space-y-3 text-sm">
                {[
                  { k: '作品名称', v: comic.title },
                  { k: '作者', v: comic.author },
                  { k: '分类', v: comic.category },
                  { k: '连载状态', v: comic.status },
                  { k: '更新时间', v: comic.updated },
                  { k: '作品评分', v: `${comic.rating} 分` },
                ].map((item) => (
                  <div key={item.k} className="flex items-center justify-between border-b border-background-100 pb-2.5">
                    <dt className="text-foreground-500">{item.k}</dt>
                    <dd className="font-medium text-foreground-800">{item.v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-2xl bg-secondary-500 p-6 text-background-50">
              <h2 className="flex items-center gap-2 font-heading text-xl">
                <i className="ri-lightbulb-flash-line text-2xl"></i>
                编辑点评
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-50/90">
                「{comic.title}」是近期{comic.category}品类里难得的潜力佳作。剧情扎实、人物立体，编辑团队强烈推荐入坑。想看更多类似好货，欢迎逛逛「{comic.category}」分类。
              </p>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="flex items-end justify-between">
              <div>
                <p className="mb-2 flex items-center gap-2 text-sm font-bold text-primary-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-500"></span>相关推荐
                </p>
                <h2 className="font-heading text-2xl text-foreground-950 md:text-4xl">猜你还想看</h2>
              </div>
              <Link
                to={`/category/${comic.categorySlug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm font-bold text-foreground-700 hover:text-primary-600"
              >
                {category?.name}全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}